/**
 * Cube Behavior
 * 
 * Basic destructible test object. Takes damage from character collisions
 * and transitions through states: idle → breaking → debris.
 */

import { EntityType, EntityTypeHelpers } from '../../types/EntityTypes';
import { crateWoodenMachine } from '../../state/assetMachines';
import { logger } from '../../core/Logger';
import type { EntityBehavior } from '../../core/BehaviorRegistry';
import { stoneDust } from '../../systems/particles';

export const cubeBehavior: EntityBehavior = {
  id: 'object:cube',
  displayName: 'Cube',
  type: EntityType.DYNAMIC_OBJECT,
  category: 'object',
  tags: ['test', 'cube', 'destructible'],
  
  createMachine: () => crateWoodenMachine,
  
  onSpawn: (api) => {
    logger.assets.debug(`Cube ${api.self.id} spawned at (${api.position.x.toFixed(1)}, ${api.position.y.toFixed(1)}, ${api.position.z.toFixed(1)})`);
  },
  
  onCollision: (api) => {
    const { ctx, actions } = api;
    
    // Apply damage when hit by characters
    if (EntityTypeHelpers.isCharacter(ctx.other)) {
      const damage = Math.min(50, Math.max(10, ctx.impulse * 10));
      logger.assets.debug(
        `Cube ${ctx.self.id} hit by ${ctx.other.displayName || ctx.other.id} ` +
        `(player=${ctx.other.isPlayer}) at ${ctx.impulse.toFixed(2)} m/s, ` +
        `applying ${damage.toFixed(1)} damage`
      );
      actions.applyDamage(10);
      
      // Spawn impact particles at the contact point
      actions.spawnParticles('impact_wood', { position: ctx.contactPoint });
    }
    
    // Take more damage from projectiles
    if (EntityTypeHelpers.isProjectile(ctx.other)) {
      logger.assets.debug(`Cube ${ctx.self.id} hit by projectile ${ctx.other.id}`);
      actions.applyDamage(25);
      
      // Spawn damage sparks for projectile hits
      actions.spawnParticles('damage_spark', { position: ctx.contactPoint });
    }
    
    // Small damage from fast-moving debris
    if (EntityTypeHelpers.isDebris(ctx.other) && ctx.impulse > 3) {
      logger.assets.debug(`Cube ${ctx.self.id} hit by debris at ${ctx.impulse.toFixed(2)} m/s`);
      actions.applyDamage(5);
    }
  },
  
  onDamage: (api) => {
    logger.assets.debug(`Cube ${api.self.id} took ${api.amount.toFixed(1)} damage`);
    return undefined; // Don't modify damage
  },
  
  onBreak: (api) => {
    logger.assets.debug(`Cube ${api.self.id} is breaking! Spawning explosion particles`);
    api.actions.spawnParticles('explosion_debris', { position: api.position });
    
    // Trigger screen impact effect for visual feedback
    api.actions.triggerImpactEffect('medium', 0.5);
  },
  
  // Configure debris particles to use wood dust effect
  debrisParticle: () => stoneDust({
    color: { r: 0.9, g: 0.4, b: 0.2 },
  }),
  
  onDestroy: (api) => {debugger
    logger.assets.debug(`Cube ${api.self.id} destroyed (cause: ${api.cause})`);
  },
};
